import { GridRowParams } from '@mui/x-data-grid-premium';
import { parseISO } from 'date-fns';
import { findIndex, fromPairs, get } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Note, Production, Reference } from 'sr-types/lib/search/v1/graphql';
import { onSaveEntityAfterAttributeEdit } from '../attributes/attributesUtils';
import Attachments from '../common/attachments/Attachments';
import { SECURITY_ACTIONS, useEntitySecurityEvaluator, type EntityName } from '../common/auth/Security';
import { UserContext } from '../common/auth/UserContext';
import { MainSidePanelProps } from '../common/components/layout/LayoutUtils';
import WithSidePanel from '../common/components/layout/WithSidePanel';
import { FILTER_IDENTIFIERS, SIDE_PANEL_IDS, SIZES, TEMP_ID_PREFIX } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { useInitializeListViewDeepLinkUIState } from '../common/deepLink';
import type { DeepLinkUIStateOnBeforeInitializationHandler } from '../common/deepLink/types';
import { useOnMobile } from '../common/hooks/breakpoints';
import { useBrowserOrAccountLocale } from '../common/hooks/useBrowserOrAccountLocale';
import I18n from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import { V } from '../common/Layout';
import DataGrid from '../common/list/DataGrid';
import type { DataGridCustomRowAction } from '../common/list/DataGrid/types';
import StatusReportsPanel from '../common/list/StatusReportsPanel';
import { PROFILES, RESERVATION_PROFESSIONAL, SUPPLIER } from '../common/nav/apps';
import Notes from '../common/notes/Notes';
import { toYearFromISOString } from '../common/utils/dateTime';
import useHistory from '../common/utils/useHistory';
import ProductionDataListConfiguration from './DataList/ProductionDataListConfiguration/ProductionDataListConfiguration';
import CreateProductionForm from './form/CreateProductionForm';
import { useSaveProduction } from './helpers/productionApi';
import { onTagChanged, Production_Field_Names } from './helpers/productionUtils';
import useProductionGridColumns from './helpers/useProductionGridColumns';
import { useAssociateStatusNoteToProduction } from './hooks/associateProductionStatusNote';
import ProductionAttributesSidePanel from './ProductionAttributesSidePanel';
import { STATE_TYPE } from './productionConstants';
import { ProductionModes } from './ProductionList';
import { getItemsWithNewStatusNote } from './productionUtils/productionListViewUtils';

export type OpenPanelType = 'notes' | 'attachments' | 'attributes' | null;

export function ProductionDataGrid({
    entity,
    feature,
    data: raw,
    uri,
    history,
    onChangeData,
    controlledSidePanelId,
    onControlledSidePanelClose,
    onNotesPanelToggle,
    onPanelTypeChange,
    isConfigurationClicked,
    handleConfigurationState,
    setCustomVariables,
    reportSidePanelContextTerm,
    ...props
}) {
    const { application } = useContext(UserContext);
    const { changeRoute, searchParams } = useHistory();
    const { associateProductionStatusNote, statusNoteSaving } = useAssociateStatusNoteToProduction();
    const { onSaveProduction } = useSaveProduction();

    const isMobile = useOnMobile();
    const locale = useBrowserOrAccountLocale();

    const productionMode = searchParams.get('productionMode');
    const isProductionMode = useMemo(() => {
        return productionMode === ProductionModes.production;
    }, [productionMode]);

    const evaluateEntitySecurity = useEntitySecurityEvaluator();
    const defaultColumns = useProductionGridColumns(isProductionMode);

    const [editingId, setEditingId] = useState<string>();
    const [entityReference, setEntityReference] = useState<Reference>();
    const [rowEntityName, setRowEntityName] = useState<string>();
    const [sidePanelOpened, setSidePanelOpened] = useState<string>();
    const [currentStatusNote, setCurrentStatusNote] = useState<Note>();
    const [focusField, setFocusField] = useState<string>();
    const [isStatusNoteAction, setIsStatusNoteAction] = useState<boolean>(false);
    const [deletedStatusNoteId, setDeletedStatusNoteId] = useState<string | null>(null);
    const [isNoteSectionExtended, setIsNoteSectionExtended] = useState<boolean>(false);
    const [noteEditorFocusTrigger, setNoteEditorFocusTrigger] = useState<number>(0);

    //add new status note - hide notes if it's status note action and there's no current status note associated
    const hideNotes = useMemo(() => isStatusNoteAction && !currentStatusNote, [isStatusNoteAction, currentStatusNote]);

    //open existing status note for viewing/editing - hide search and editor if it's status note action and there's a current status note associated
    const hideSearchAndEditor = useMemo(
        () => isStatusNoteAction && !!currentStatusNote,
        [isStatusNoteAction, currentStatusNote]
    );

    // Track Notes panel state and notify parent
    useEffect(() => {
        const isNotesOpen = sidePanelOpened === SIDE_PANEL_IDS.notes || controlledSidePanelId === SIDE_PANEL_IDS.notes;
        onNotesPanelToggle?.(isNotesOpen);
    }, [sidePanelOpened, controlledSidePanelId, onNotesPanelToggle]);

    // Track panel type and notify parent
    useEffect(() => {
        const currentPanelId = controlledSidePanelId || sidePanelOpened;
        let panelType: OpenPanelType | null = null;

        if (currentPanelId === SIDE_PANEL_IDS.notes) {
            panelType = SIDE_PANEL_IDS.notes;
        } else if (currentPanelId === SIDE_PANEL_IDS.attachments) {
            panelType = SIDE_PANEL_IDS.attachments;
        } else if (currentPanelId === SIDE_PANEL_IDS.attributes) {
            panelType = SIDE_PANEL_IDS.attributes;
        }

        onPanelTypeChange?.(panelType);
    }, [sidePanelOpened, controlledSidePanelId, onPanelTypeChange]);

    const handleSidePanelOpening = useCallback(
        (row: Production, panelId: string, note?: Note, fromStatusNoteActionClick?: boolean) => {
            onControlledSidePanelClose?.();
            setEntityReference(row.reference);
            setSidePanelOpened(panelId);
            setRowEntityName(`${row[FILTER_IDENTIFIERS.name]}`);
            if (fromStatusNoteActionClick) {
                setCurrentStatusNote(note);
            }
            setIsStatusNoteAction(fromStatusNoteActionClick || false);
            if (!note && fromStatusNoteActionClick) {
                setNoteEditorFocusTrigger((prev) => prev + 1);
            }
        },
        [onControlledSidePanelClose]
    );

    const columnVisibility = useMemo(
        () =>
            fromPairs(defaultColumns.filter((column) => get(column, 'hide') === true).map((key) => [key.field, false])),
        [defaultColumns]
    );

    const rows = useMemo(() => {
        if (!raw?.results?.hits?.items) return [];

        return raw.results.hits.items.map((entity, index) => {
            const otherTitlesValue = entity.otherTitles?.find((title) => title.key === 'AKA')?.value ?? '';
            const entityReleaseDate = entity.releaseDate ? toYearFromISOString(entity.releaseDate, locale) : '';
            const seasonNumber = entity.showAttributes?.find((attribute) => attribute.key === 'seasonNo')?.value ?? '';

            return {
                key: index,
                ...entity,
                publictitle: entity.name,
                productiontype: entity.productionType,
                productionStatus: entity.productionStatus,
                otherTitles: otherTitlesValue,
                releaseDate: parseISO(entity.releaseDate),
                releaseYear: entityReleaseDate,
                ProductionYears: entity.productionYears,
                seasonNumber,
                productionGroup: entity.productionGroup?.label || entity.productionGroup?.productionReference?.id,
                createdOn: parseISO(entity.createdOn),
                updatedOn: parseISO(entity.updatedOn),
                security: entity.security,
                statusNote: entity.currentStatusNote,
                _raw: entity
            };
        });
    }, [raw?.results?.hits?.items, locale]);

    const columns = useMemo(() => {
        const defaultCols = defaultColumns.map((col) => ({
            ...col,
            sortable: findIndex(raw?.results?.summary?.sortOptions, { id: col.field }) > -1
        }));
        return defaultCols;
    }, [defaultColumns, raw?.results?.summary?.sortOptions]);

    const finalRows = useMemo(() => (raw?.results ? rows : []), [raw?.results, rows]);

    const rowCount = useMemo(() => raw?.results?.summary?.total ?? 0, [raw?.results?.summary?.total]);

    const entitySecurityProviderProps: MainSidePanelProps['entitySecurityProviderProps'] = useMemo(() => {
        const entityName = ((entity || entityReference?.type) as EntityName) ?? ENTITY_CONSTANTS.production;
        const entitySecurity = rows.find((row) => row.reference.id === entityReference?.id)?.security ?? {};
        return {
            entityName,
            entitySecurity
        };
    }, [entity, entityReference?.id, entityReference?.type, rows]);

    const onStatusNoteChanged = useCallback(
        (note: Note | null, productionId: string) => {
            if (!note && currentStatusNote?.id) {
                setDeletedStatusNoteId(currentStatusNote.id);
            } else if (note) {
                setDeletedStatusNoteId(null);
            }
            setCurrentStatusNote(note);
            const newItems = raw?.results?.hits?.items.map((item: Production) =>
                getItemsWithNewStatusNote(item, productionId, note)
            );
            onChangeData(newItems);
        },
        [currentStatusNote?.id, onChangeData, raw?.results?.hits?.items]
    );

    const handleAssociateStatusNoteMutationFail = useCallback(
        (localCurrentStatusNote: Note, productionId: string) => {
            onStatusNoteChanged(localCurrentStatusNote, productionId);
        },
        [onStatusNoteChanged]
    );

    const handleOnAssociateStatusNote = useCallback(
        (note: Note) => {
            const localCurrentStatusNote = currentStatusNote;
            onStatusNoteChanged(note, entityReference.id);
            setCurrentStatusNote(note);
            if (note?.reference?.id?.startsWith(TEMP_ID_PREFIX)) return;

            associateProductionStatusNote({
                productionId: entityReference.id,
                note: note.reference
            })
                .then((reference: Reference) => {
                    if (!reference?.id) {
                        handleAssociateStatusNoteMutationFail(localCurrentStatusNote, entityReference.id);
                    }
                })
                .catch(() => handleAssociateStatusNoteMutationFail(localCurrentStatusNote, entityReference.id));
        },
        [
            currentStatusNote,
            entityReference?.id,
            associateProductionStatusNote,
            onStatusNoteChanged,
            handleAssociateStatusNoteMutationFail
        ]
    );

    const resetEditingState = useCallback(() => {
        setEditingId(undefined);
        setFocusField(undefined);
    }, []);

    const onAttributes = useCallback(
        (row: Production) => {
            handleSidePanelOpening(row, SIDE_PANEL_IDS.attributes);
        },
        [handleSidePanelOpening]
    );

    const onAttachments = useCallback(
        (row: Production) => {
            handleSidePanelOpening(row, SIDE_PANEL_IDS.attachments);
        },
        [handleSidePanelOpening]
    );

    const onNotes = useCallback(
        (row: Production) => {
            handleSidePanelOpening(row, SIDE_PANEL_IDS.notes);
            setCurrentStatusNote(row.currentStatusNote);
        },
        [handleSidePanelOpening]
    );

    const onTags = useCallback((row: Production) => {
        setEditingId(row.id);
        setFocusField(Production_Field_Names.tags);
    }, []);

    const onDetails = useCallback(
        (id: string, row: Production) => {
            if ([SUPPLIER.home, RESERVATION_PROFESSIONAL.home].includes(application.home)) {
                changeRoute(`${application.home}/production/${id}/details`);
            } else if (application.home === PROFILES.home) {
                const {
                    allowed: [canRead, canEdit]
                } = evaluateEntitySecurity([SECURITY_ACTIONS.READ, SECURITY_ACTIONS.EDIT], {
                    entityName: ENTITY_CONSTANTS.production,
                    entitySecurity: row.security
                });
                const isReadOnly = canRead && !canEdit;
                changeRoute(`${application.home}/production/${id}/details`, undefined, false, {
                    isReadOnly: isReadOnly
                });
            } else if (productionMode === ProductionModes.production) {
                const routePrefix = row?.managedProduction ? 'workspace' : 'production';
                const mode = row?.managedProduction ? ProductionModes.workspace : ProductionModes.production;

                changeRoute(`${application.home}/${routePrefix}/${id}/details`, {
                    productionMode: mode
                });
            } else {
                changeRoute(`${application.home}/workspace/${id}/details`, {
                    productionMode: ProductionModes.workspace,
                    'prod-details_tab': '0'
                });
            }
        },
        [application.home, changeRoute, evaluateEntitySecurity, productionMode]
    );

    const onEdit = useCallback(
        (id: string, row?: Production) => {
            if (productionMode === ProductionModes.production && row && !row.managedProduction) {
                onDetails(id, row);
                return;
            }
            setEditingId(id);
        },
        [productionMode, onDetails]
    );

    // Full set of row actions for production data grid:
    // - Attributes (EDIT): manage production-specific attributes
    // - Tags (EDIT): manage production tags
    // - Attachments (COMMENT): view/manage production attachments
    // - Notes (COMMENT): view/create production notes
    const customRowActions = useMemo((): DataGridCustomRowAction[] => {
        if (isProductionMode) {
            return [];
        }

        return [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                labelToken: 'attributes.title',
                icon: <Icons.ResourceFields fontSize={SIZES.small} />,
                onClick: onAttributes
            },
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                labelToken: 'common.label.tags',
                icon: <Icons.LabelOutlined fontSize="small" />,
                onClick: onTags
            },
            {
                securityAction: SECURITY_ACTIONS.COMMENT,
                labelToken: 'attachments.title',
                icon: <Icons.AttachFileOutlined fontSize="small" />,
                onClick: onAttachments
            },
            {
                securityAction: SECURITY_ACTIONS.COMMENT,
                labelToken: 'notes.title',
                icon: <Icons.Notes fontSize="small" />,
                onClick: onNotes
            }
        ];
    }, [isProductionMode, onAttributes, onTags, onAttachments, onNotes]);

    const listViewColDef = useMemo(() => {
        if (!props?.listViewConfiguration?.listViewColDef) return undefined;

        const parentColDef = props.listViewConfiguration.listViewColDef;

        const memoizedRenderCell = (params: GridRowParams) =>
            parentColDef.renderCell?.({
                ...params,
                onEdit: () => onEdit(params.row.id, params.row),
                onNotes: () => onNotes(params.row),
                onAttachments: () => onAttachments(params.row),
                onCustomAttribute: () => onAttributes(params.row),
                onTagHandle: () => onTags(params.row),
                handleSidePanelOpening: (
                    row: Production,
                    panelId: string,
                    note?: Note,
                    fromStatusNoteActionClick?: boolean
                ) => handleSidePanelOpening(row, panelId, note, fromStatusNoteActionClick),
                onStatusNoteChanged: (note: Note | null, productionId: string) =>
                    onStatusNoteChanged(note, productionId),
                changeDeletedStatusNoteId: (noteId: string | null) => setDeletedStatusNoteId(noteId)
            });

        return {
            ...parentColDef,
            renderCell: memoizedRenderCell
        };
    }, [
        props?.listViewConfiguration?.listViewColDef,
        onEdit,
        onNotes,
        onAttachments,
        onAttributes,
        onTags,
        handleSidePanelOpening,
        onStatusNoteChanged,
        setDeletedStatusNoteId
    ]);

    const onBeforeInitializeDeepLinkUIState: DeepLinkUIStateOnBeforeInitializationHandler = useCallback(
        ({ entityId, sidePanelId }) => {
            if (!entityId || !sidePanelId) {
                return;
            }

            const matchingRow = rows.find((row) => row.reference?.id === entityId);

            if (matchingRow?.reference) {
                handleSidePanelOpening(matchingRow, sidePanelId);
                if (sidePanelId === SIDE_PANEL_IDS.notes) {
                    const matchingRowWithStatusNote = matchingRow as Production & { statusNote?: Note };
                    setCurrentStatusNote(matchingRowWithStatusNote.statusNote ?? undefined);
                }
            }
        },
        [handleSidePanelOpening, rows]
    );

    const getDeepLinkEntityDataRecordId = useCallback((row: Production) => row.reference?.id, []);

    useInitializeListViewDeepLinkUIState<Production>({
        entity: ENTITY_CONSTANTS.production,
        entityDataList: raw?.results?.hits?.items ? (rows as Production[]) : undefined,
        selectedEntityDataRecordId: entityReference?.id,
        getEntityDataRecordId: getDeepLinkEntityDataRecordId,
        onBeforeInitialize: onBeforeInitializeDeepLinkUIState
    });

    const securedPanels = useMemo(
        () => [
            {
                id: SIDE_PANEL_IDS.attributes,
                icon: Icons.ResourceFields,
                label: <I18n token="attributes.title" />,
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => {
                    if (!entityReference?.id) {
                        return null;
                    }

                    return (
                        <ProductionAttributesSidePanel
                            entityId={entityReference.id}
                            onClose={() => {
                                setEntityReference(undefined);
                                setRowEntityName(undefined);
                                setSidePanelOpened(undefined);
                                onClose();
                            }}
                            entityName={rowEntityName ?? ''}
                            entity={ENTITY_CONSTANTS.production}
                            onSaveEntity={(updatedValues, isDeletingAttribute) =>
                                onSaveEntityAfterAttributeEdit(
                                    updatedValues,
                                    isDeletingAttribute,
                                    onSaveProduction,
                                    entityReference.id,
                                    locale
                                )
                            }
                        />
                    );
                }
            },
            {
                id: 'notes',
                icon: Icons.Notes,
                label: <I18n token="notes.title" />,
                securityAction: SECURITY_ACTIONS.COMMENT,
                entityName: entitySecurityProviderProps.entityName,
                disabled: !entityReference,
                component: (onClose) => (
                    <Notes
                        key={entityReference?.id}
                        id={'notes-production'}
                        title={<I18n token="notes.panel.title" values={{ entityName: rowEntityName }} />}
                        entity={entitySecurityProviderProps.entityName}
                        entityReference={entityReference}
                        onClose={() => {
                            setEntityReference(undefined);
                            setSidePanelOpened(undefined);
                            onClose();
                        }}
                        onAssociateStatusNote={{
                            saveFunction: (note: Note) => handleOnAssociateStatusNote(note),
                            loadingState: statusNoteSaving,
                            statusNote: currentStatusNote,
                            onStatusNoteChanged,
                            deletedStatusNoteId,
                            isStatusNoteAction
                        }}
                        singleNoteView={
                            isStatusNoteAction && currentStatusNote ? { noteData: currentStatusNote } : undefined
                        }
                        customSidePanelActions={[
                            {
                                icon: isNoteSectionExtended ? Icons.ShrinkIcon : Icons.ExpandIcon,
                                label: <I18n token={isNoteSectionExtended ? 'dialog.retract' : 'dialog.extend'} />,
                                onClick: () => setIsNoteSectionExtended((prev) => !prev)
                            }
                        ]}
                        showNotes={!hideNotes}
                        hideSearchAndEditor={hideSearchAndEditor}
                        focusTrigger={noteEditorFocusTrigger}
                    />
                )
            },
            {
                id: 'attachments',
                icon: Icons.Attachments,
                label: <I18n token="attachments.title" />,
                securityAction: SECURITY_ACTIONS.COMMENT,
                entityName: entitySecurityProviderProps.entityName,
                tooltip: entityReference ? <I18n token="atttachments.close" /> : <I18n token="attachment.from.grid" />,
                disabled: !entityReference,
                component: (onClose) => (
                    <Attachments
                        id={'attachments-production'}
                        entityReference={entityReference}
                        title={<I18n token="attachments.panel.title" values={{ entityName: rowEntityName }} />}
                        onClose={() => {
                            setEntityReference(undefined);
                            setSidePanelOpened(undefined);
                            onClose();
                        }}
                        entity={entitySecurityProviderProps.entityName}
                    />
                )
            },
            {
                id: SIDE_PANEL_IDS.statusReports,
                icon: Icons.Visibility,
                hidden: true,
                securityAction: SECURITY_ACTIONS.READ,
                entityName: entitySecurityProviderProps.entityName,
                component: (onClose) => (
                    <StatusReportsPanel
                        contextTerm={reportSidePanelContextTerm}
                        featureName={feature}
                        onClose={() => {
                            onControlledSidePanelClose?.();
                            setEntityReference(undefined);
                            setRowEntityName(undefined);
                            setSidePanelOpened(undefined);
                            onClose();
                        }}
                    />
                )
            }
        ],
        [
            currentStatusNote,
            deletedStatusNoteId,
            entityReference,
            entitySecurityProviderProps.entityName,
            feature,
            handleOnAssociateStatusNote,
            hideNotes,
            hideSearchAndEditor,
            isNoteSectionExtended,
            isStatusNoteAction,
            locale,
            noteEditorFocusTrigger,
            onControlledSidePanelClose,
            onSaveProduction,
            onStatusNoteChanged,
            reportSidePanelContextTerm,
            rowEntityName,
            statusNoteSaving
        ]
    );

    return (
        <WithSidePanel
            visibleSidePanelId={controlledSidePanelId || (entityReference ? sidePanelOpened : undefined)}
            entitySecurityProviderProps={entitySecurityProviderProps}
            enableDeepLink
            securedPanels={securedPanels}
            leftToolbar={undefined}
            hideHeader={true}
            expandView={isNoteSectionExtended}
        >
            <V sx={{ height: '100%', minWidth: '1px' }}>
                <DataGrid
                    id={'Production'}
                    {...props}
                    asIsColumns={['managed', 'createdFrom', 'avatar']}
                    clickableColumn={'publictitle'}
                    feature={feature}
                    entityName={entity as EntityName}
                    rowCount={rowCount}
                    rows={finalRows}
                    columns={columns}
                    forceDefaultColumnVisibility
                    defaultColumnVisibility={columnVisibility}
                    sortingMode={'server'}
                    disableColumnSorting={!isProductionMode}
                    sortModel={isProductionMode ? props.sortModel : undefined}
                    onSortModelChange={isProductionMode ? props.onSortModelChange : undefined}
                    onDetails={onDetails}
                    customRowActions={customRowActions}
                    onEdit={onEdit}
                    listViewConfiguration={{
                        enableListView: props?.listViewConfiguration?.enableListView,
                        rowBufferPx: props?.listViewConfiguration?.rowBufferPx,
                        listViewColDef
                    }}
                    containerSx={{
                        '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
                            display: 'none'
                        }
                    }}
                />
            </V>
            {!isProductionMode && isConfigurationClicked && (
                <ProductionDataListConfiguration
                    entity={ENTITY_CONSTANTS.workspace}
                    handleConfigurationModalState={handleConfigurationState}
                    setCustomVariables={setCustomVariables}
                />
            )}
            {!!editingId && (
                <CreateProductionForm
                    title={<I18n token={'production.form.edit.title'} />}
                    isOpen={!!editingId}
                    productionId={editingId}
                    focusField={focusField}
                    onCancel={resetEditingState}
                    onSave={(productionId, production) => {
                        onTagChanged(raw?.results?.hits?.items || [], production, onChangeData);
                        resetEditingState();
                    }}
                    state={isProductionMode ? undefined : STATE_TYPE.managed}
                />
            )}
        </WithSidePanel>
    );
}
